import { useRecoilState, useRecoilValue } from 'recoil';
import useCompositeCbctBackendTask from '~/hooks/workflows/backend/tasks/useCompositeCbctBackendTask';
import { useImageTranslation } from '~/hooks/workflows/backend/tasks/useImageTranslation';
import { useImageTranslationModels } from '~/hooks/workflows/backend/tasks/useImageTranslationModels';
import {
    replanningPreparationState,
    replanningSyntheticRunState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import type { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { isJobRunning, jobQueueState } from '~/state/atoms/jobAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { usePlanAndTargetScans } from './usePlanAndTargetScans';

export interface SyntheticImagingRun {
    readonly models: readonly PredictionModel[];
    readonly run: () => void;
    readonly canRun: boolean;
    readonly isRunning: boolean;
    readonly owesRun: boolean;
}

export function useSyntheticImagingRun(): SyntheticImagingRun {
    const preparation = useRecoilValue(replanningPreparationState);
    const [record, setRecord] = useRecoilState(replanningSyntheticRunState);
    const { startProcessing, isSubmitting } = useImageTranslation();
    const compositeTask = useCompositeCbctBackendTask();
    const { source, target } = usePlanAndTargetScans();
    const { jobs } = useRecoilValue(jobQueueState);

    const { generateSynthetic, syntheticModelName, compositeCbct } =
        preparation;
    const models = useImageTranslationModels(target?.firstSlice?.modality);
    const chosen = models.find((model) => model.name === syntheticModelName);

    const isRunning =
        isSubmitting ||
        compositeTask.isSubmitting ||
        jobs.some(
            (job) =>
                (job.type === WorkflowTypes.IMAGE ||
                    job.type === WorkflowTypes.COMPOSITE_CBCT) &&
                isJobRunning(job.status)
        );

    const producedTheseSettings =
        !!record &&
        record.composite === compositeCbct &&
        record.modelName === syntheticModelName &&
        jobs.some((job) => job.type === record.type && job.status === 'Done');

    const canRun =
        !isRunning && !!(compositeCbct ? target && source : chosen && target);
    const owesRun = generateSynthetic && !producedTheseSettings;

    const run = () => {
        if (!canRun || !target) {
            return;
        }
        if (compositeCbct) {
            if (!source) {
                return;
            }
            setRecord({
                type: WorkflowTypes.COMPOSITE_CBCT,
                modelName: syntheticModelName,
                composite: true,
            });
            void compositeTask.startProcessing({
                cbctImage: target,
                baseCtImage: source,
            });
            return;
        }
        if (!chosen) {
            return;
        }
        setRecord({
            type: WorkflowTypes.IMAGE,
            modelName: syntheticModelName,
            composite: false,
        });
        void startProcessing(chosen, undefined, undefined, target);
    };

    return { models, run, canRun, isRunning, owesRun };
}
